import type { CSSProperties } from "react";

const HEX_RE = /^#?([0-9a-f]{6})$/i;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(input: string | null | undefined): Rgb | null {
  if (!input) return null;
  const match = HEX_RE.exec(input.trim());
  if (!match) return null;
  const hex = match[1];
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function mix(a: Rgb, b: Rgb, weight: number): Rgb {
  return {
    r: a.r * (1 - weight) + b.r * weight,
    g: a.g * (1 - weight) + b.g * weight,
    b: a.b * (1 - weight) + b.b * weight,
  };
}

function luminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function readableInk(color: Rgb): string {
  return luminance(color) > 0.5
    ? toHex(mix(color, { r: 0, g: 0, b: 0 }, 0.55))
    : toHex(mix(color, { r: 0, g: 0, b: 0 }, 0.18));
}

export function tenantAccentStyle(
  primaryColor: string | null | undefined,
): CSSProperties {
  const color = parseHex(primaryColor);
  if (!color) return {};
  return {
    "--accent": toHex(color),
    "--accent-ink": readableInk(color),
    "--accent-bg": toHex(mix(color, { r: 255, g: 255, b: 255 }, 0.9)),
    "--accent-border": toHex(mix(color, { r: 255, g: 255, b: 255 }, 0.68)),
  } as CSSProperties;
}

